import React from "react";
import { getListQueryStatus, QueryStatus } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	Button,
	Column,
	compareObjects,
	compareStringsAsc,
	compareStringsDesc,
	Conditional,
	Else,
	Heading,
	If,
	isBefore,
	isSameTime,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	Section,
	stringToLocalDate,
	toLocalDateString,
} from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import { Icon } from "../../../../Components/Icon";
import {
	CourseSection,
	CourseSectionCourseAssessmentPreviewSummaryListQuery,
	CourseSectionCourseAssessmentSummaryListQuery,
	useCourseSectionCourseAssessmentSummaryListQuery,
	useStudentCountQuery,
} from "../../../../Utilities";
import { CourseAssessmentCategoryFormat, CourseAssessmentFormat, CourseAssessmentTimingFormat } from "../../../Format";
import { CourseAssessmentCreateModal } from "../CreateModal";
import { ControlledCourseAssessmentModal } from "../Modal";
import styles from "./styles.module.css";

export type ControlledCourseSectionCourseAssessmentsListModalProps = {
	courseSectionID: CourseSection["id"];
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ControlledCourseSectionCourseAssessmentsListModal({
	courseSectionID,
	ifRef,
	onClose,
	className,
	style,
}: ControlledCourseSectionCourseAssessmentsListModalProps) {
	const { data: studentCountData } = useStudentCountQuery({
		variables: { filters: { studentCourseSectionEnrollments: [{ courseSectionIDEQ: courseSectionID }] } },
	});
	const studentCount = studentCountData?.studentConnection.totalCount ?? 0;

	const { data, loading, error } = useCourseSectionCourseAssessmentSummaryListQuery({
		variables: {
			courseSectionID,
			filters: {
				courseAssessmentTimings: [
					{
						courseSectionIDEQ: courseSectionID,
						// TODO pagination
					},
				],
			},
		},
	});

	const { dueToday, upcoming, noDueDate, past } = React.useMemo(() => {
		const dueToday = [];
		const upcoming = [];
		const noDueDate = [];
		const past = [];

		const today = new Date();
		for (let i = 0; i < (data?.courseAssessmentConnection.edges.length ?? 0); i++) {
			const courseAssessment = data?.courseAssessmentConnection.edges[i].node;
			if (!courseAssessment) {
				continue;
			}
			const dueOn = courseAssessment?.courseAssessmentTimings[0]?.dueOn;
			if (!dueOn) {
				noDueDate.push(courseAssessment);
				continue;
			}
			const isDueToday = isSameTime(today, stringToLocalDate(dueOn, "day"), "day");
			if (isDueToday) {
				dueToday.push(courseAssessment);
				continue;
			}
			const isDueInFuture = isBefore(new Date(), stringToLocalDate(dueOn, "day"), "day");
			if (isDueInFuture) {
				upcoming.push(courseAssessment);
				continue;
			}
			past.push(courseAssessment);
		}

		upcoming.sort(compareObjects((e) => e.courseAssessmentTimings[0].dueOn, compareStringsAsc()));
		past.sort(compareObjects((e) => e.courseAssessmentTimings[0].dueOn, compareStringsDesc()));

		return { dueToday, upcoming, noDueDate, past };
	}, [data]);

	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "courseAssessmentConnection" });

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["modal--upcoming"], className)}
			style={style}
		>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<Heading.H2 noMargin>Assessments</Heading.H2>

					<CourseAssessmentCreateController courseSectionID={courseSectionID} />
				</Row>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<CourseAssessmentsSection
						heading="Due Today"
						courseAssessments={dueToday}
						courseSectionID={courseSectionID}
						studentCount={studentCount}
						queryStatus={queryStatus}
					/>
					<CourseAssessmentsSection
						heading="Upcoming"
						courseAssessments={upcoming}
						courseSectionID={courseSectionID}
						studentCount={studentCount}
						queryStatus={queryStatus}
					/>
					<CourseAssessmentsSection
						heading="No Due Date"
						courseAssessments={noDueDate}
						courseSectionID={courseSectionID}
						studentCount={studentCount}
						queryStatus={queryStatus}
					/>
					<CourseAssessmentsSection
						heading="Past"
						courseAssessments={past}
						courseSectionID={courseSectionID}
						studentCount={studentCount}
						queryStatus={queryStatus}
					/>
				</Column>
			</Modal.Body>
		</Modal>
	);
}

export type ControlledCourseSectionUpcomingCourseAssessmentsListModalProps = {
	courseSectionID: CourseSection["id"];
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ControlledCourseSectionUpcomingCourseAssessmentsListModal({
	courseSectionID,
	ifRef,
	onClose,
	className,
	style,
}: ControlledCourseSectionUpcomingCourseAssessmentsListModalProps) {
	const { data: studentCountData } = useStudentCountQuery({
		variables: { filters: { studentCourseSectionEnrollments: [{ courseSectionIDEQ: courseSectionID }] } },
	});
	const studentCount = studentCountData?.studentConnection.totalCount ?? 0;

	const { data, loading, error } = useCourseSectionCourseAssessmentSummaryListQuery({
		variables: {
			courseSectionID,
			filters: {
				courseAssessmentTimings: [
					{
						courseSectionIDEQ: courseSectionID,
						or: [
							{
								dueOnGTE: toLocalDateString(new Date()),
								dueOnLTE: toLocalDateString(addTimeToDate(new Date(), [1, "week"])),
							},
							{ dueOnIsNull: true },
						],
					},
				],
			},
		},
	});

	const { dueToday, dueSoon, noDueDate } = React.useMemo(() => {
		const dueToday = [];
		const dueSoon = [];
		const noDueDate = [];

		const today = new Date();
		for (let i = 0; i < (data?.courseAssessmentConnection.edges.length ?? 0); i++) {
			const courseAssessment = data?.courseAssessmentConnection.edges[i].node;
			if (!courseAssessment) {
				continue;
			}
			const dueOn = courseAssessment?.courseAssessmentTimings[0]?.dueOn;
			if (!dueOn) {
				noDueDate.push(courseAssessment);
				continue;
			}
			const isDueToday = isSameTime(today, stringToLocalDate(dueOn, "day"), "day");
			if (isDueToday) {
				dueToday.push(courseAssessment);
				continue;
			}
			dueSoon.push(courseAssessment);
		}

		dueSoon.sort(compareObjects((e) => e.courseAssessmentTimings[0].dueOn, compareStringsAsc()));

		return { dueToday, dueSoon, noDueDate };
	}, [data]);

	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "courseAssessmentConnection" });

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["modal--upcoming"], className)}
			style={style}
		>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<Heading.H2 noMargin>Upcoming Assessments</Heading.H2>

					<CourseAssessmentCreateController courseSectionID={courseSectionID} />
				</Row>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<CourseAssessmentsSection
						heading="Due Today"
						courseAssessments={dueToday}
						courseSectionID={courseSectionID}
						studentCount={studentCount}
						queryStatus={queryStatus}
					/>
					<CourseAssessmentsSection
						heading="Due Soon"
						courseAssessments={dueSoon}
						courseSectionID={courseSectionID}
						studentCount={studentCount}
						queryStatus={queryStatus}
					/>
					<CourseAssessmentsSection
						heading="No Due Date"
						courseAssessments={noDueDate}
						courseSectionID={courseSectionID}
						studentCount={studentCount}
						queryStatus={queryStatus}
					/>
				</Column>
			</Modal.Body>
		</Modal>
	);
}

type CourseAssessmentsSectionProps = {
	heading: React.ReactNode;
} & CourseAssessmentsTableProps;

function CourseAssessmentsSection({ heading, ...props }: CourseAssessmentsSectionProps) {
	return (
		<Section className={styles["course-assessments-section"]}>
			<Section.Header>
				<Heading.H3 noMargin>{heading}</Heading.H3>
			</Section.Header>
			<Section.Body>
				<CourseAssessmentsTable {...props} />
			</Section.Body>
		</Section>
	);
}

const columns = ["name", "category", "dueOn", "turnedInCount", "gradedCount", "assignedCount"];

type CourseAssessmentsTableProps = {
	courseAssessments: CourseSectionCourseAssessmentSummaryListQuery["courseAssessmentConnection"]["edges"][0]["node"][];
	courseSectionID: CourseSection["id"];
	studentCount: number;
	queryStatus: QueryStatus;
};

function CourseAssessmentsTable({
	courseAssessments,
	courseSectionID,
	studentCount,
	queryStatus,
}: CourseAssessmentsTableProps) {
	return (
		<Table columns={columns} className={styles["table"]}>
			<Table.Header>
				<Table.Row>
					<Table.Heading column="name" className={styles["table__heading"]}>
						Assessment
					</Table.Heading>
					<Table.Heading column="category" className={styles["table__heading"]}>
						Type
					</Table.Heading>
					<Table.Heading column="dueOn" className={styles["table__heading"]}>
						Due Date
					</Table.Heading>
					<Table.Heading column="turnedInCount" className={styles["table__heading"]}>
						Turned In
					</Table.Heading>
					<Table.Heading column="gradedCount" className={styles["table__heading"]}>
						Graded
					</Table.Heading>
					<Table.Heading column="assignedCount" className={styles["table__heading"]}>
						Assigned
					</Table.Heading>
				</Table.Row>
			</Table.Header>
			<Table.Body>
				<Conditional>
					<If condition={courseAssessments.length > 0}>
						{courseAssessments.map((courseAssessment) => (
							<CourseAssessmentTableRow
								key={courseAssessment.id}
								courseAssessment={courseAssessment}
								courseSectionID={courseSectionID}
								studentCount={studentCount}
							/>
						))}
					</If>
					<Else>
						<Table.Row>
							<Table.Cell column={columns[0]} colspan={columns.length} className={styles["table__cell"]}>
								<Conditional>
									<If condition={queryStatus === "error"}>Something went wrong</If>
									<If condition={queryStatus === "loading"}>Loading...</If>
									<If condition={courseAssessments.length === 0}>No Assessments</If>
								</Conditional>
							</Table.Cell>
						</Table.Row>
					</Else>
				</Conditional>
			</Table.Body>
		</Table>
	);
}

type CourseAssessmentTableRowProps = {
	courseAssessment: CourseSectionCourseAssessmentSummaryListQuery["courseAssessmentConnection"]["edges"][0]["node"];
	courseSectionID: CourseSection["id"];
	studentCount: number;
};

function CourseAssessmentTableRow({ courseAssessment, courseSectionID, studentCount }: CourseAssessmentTableRowProps) {
	const timing = courseAssessment.courseAssessmentTimings[0];

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Table.Row onClick={openModal} className={styles["table__body__row"]}>
				<Table.Cell column="name" className={styles["table__cell"]}>
					<span className={styles["table__course-assessment-name"]}>
						{CourseAssessmentFormat.name(courseAssessment)}
					</span>
				</Table.Cell>
				<Table.Cell column="category" className={styles["table__cell"]}>
					{CourseAssessmentCategoryFormat.name(courseAssessment.courseAssessmentCategory)}
				</Table.Cell>
				<Table.Cell column="dueOn" className={styles["table__cell"]}>
					{timing && timing.dueOn ? CourseAssessmentTimingFormat.Fields.dueOn(timing.dueOn) : "-"}
				</Table.Cell>
				<Table.Cell column="turnedInCount" className={styles["table__cell"]}>
					{courseAssessment.courseAssessmentSubmissions.length}
				</Table.Cell>
				<Table.Cell column="gradedCount" className={styles["table__cell"]}>
					{courseAssessment.courseAssessmentMarks.length}
				</Table.Cell>
				<Table.Cell column="assignedCount" className={styles["table__cell"]}>
					{studentCount}
				</Table.Cell>
			</Table.Row>

			<Modal.If condition={isModalOpen}>
				<ControlledCourseAssessmentModal
					courseAssessmentID={courseAssessment.id}
					courseSectionID={courseSectionID}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

const previewColumns = ["name", "category", "dueOn"];

export type CourseAssessmentsPreviewTableProps = {
	courseAssessments: CourseSectionCourseAssessmentPreviewSummaryListQuery["courseAssessmentConnection"]["edges"][0]["node"][];
	courseSectionID: CourseSection["id"];
	queryStatus: QueryStatus;
};

export function CourseAssessmentsPreviewTable({
	courseAssessments,
	courseSectionID,
	queryStatus,
}: CourseAssessmentsPreviewTableProps) {
	return (
		<Table columns={previewColumns} className={styles["table"]}>
			<Table.Header>
				<Table.Row>
					<Table.Heading column="name" noResize className={styles["table__heading"]}>
						Assessment
					</Table.Heading>
					<Table.Heading column="category" noResize className={styles["table__heading"]}>
						Type
					</Table.Heading>
					<Table.Heading column="dueOn" noResize className={styles["table__heading"]}>
						Due Date
					</Table.Heading>
				</Table.Row>
			</Table.Header>
			<Table.Body>
				<Conditional>
					<If condition={courseAssessments.length > 0}>
						{courseAssessments.map((courseAssessment) => (
							<CourseAssessmentPreviewTableRow
								key={courseAssessment.id}
								courseAssessment={courseAssessment}
								courseSectionID={courseSectionID}
							/>
						))}
					</If>
					<Else>
						<Table.Row>
							<Table.Cell column={previewColumns[0]} colspan={previewColumns.length} className={styles["table__cell"]}>
								<Conditional>
									<If condition={queryStatus === "error"}>Something went wrong</If>
									<If condition={queryStatus === "loading"}>Loading...</If>
									<If condition={courseAssessments.length === 0}>No Assessments</If>
								</Conditional>
							</Table.Cell>
						</Table.Row>
					</Else>
				</Conditional>
			</Table.Body>
		</Table>
	);
}

type CourseAssessmentPreviewTableRowProps = {
	courseAssessment: CourseSectionCourseAssessmentPreviewSummaryListQuery["courseAssessmentConnection"]["edges"][0]["node"];
	courseSectionID: CourseSection["id"];
};

function CourseAssessmentPreviewTableRow({ courseAssessment, courseSectionID }: CourseAssessmentPreviewTableRowProps) {
	const timing = courseAssessment.courseAssessmentTimings[0];

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Table.Row onClick={openModal} className={styles["table__body__row"]}>
				<Table.Cell column="name" className={styles["table__cell"]}>
					<span className={styles["table__course-assessment-name"]}>
						{CourseAssessmentFormat.name(courseAssessment)}
					</span>
				</Table.Cell>
				<Table.Cell column="category" className={styles["table__cell"]}>
					{CourseAssessmentCategoryFormat.name(courseAssessment.courseAssessmentCategory)}
				</Table.Cell>
				<Table.Cell column="dueOn" className={styles["table__cell"]}>
					{timing && timing.dueOn ? CourseAssessmentTimingFormat.Fields.dueOn(timing.dueOn) : "-"}
				</Table.Cell>
			</Table.Row>

			<Modal.If condition={isModalOpen}>
				<ControlledCourseAssessmentModal
					courseAssessmentID={courseAssessment.id}
					courseSectionID={courseSectionID}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type CourseAssessmentCreateControllerProps = {
	courseSectionID: CourseSection["id"];
};

function CourseAssessmentCreateController({ courseSectionID }: CourseAssessmentCreateControllerProps) {
	const formStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				courseAssessmentTimings: [
					{ courseSectionID: courseSectionID, assignOn: toLocalDateString(new Date()), dueOn: null },
				],
			},
		}),
		[courseSectionID],
	);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant="primary" size="small" onClick={openModal}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Icon.Plus size={14} />
					Create New
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<CourseAssessmentCreateModal
					formStateArgs={formStateArgs}
					formNameSuffix={courseSectionID}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
